// src/pages/enterprise/AddEnterpriseDialog.js
// ORG-PROVISION-FROM-PANEL-01 Part B. The "Add enterprise" form. Posts to the panel
// backend's POST /api/enterprises/provision, which forwards to org-backend's internal
// provisioning door with the internal key and the staff email as createdBy.
//
// "Send the Owner's invitation now" is on by default. Off creates the organisation and
// its wallet without emailing the Owner; the result card, and the Owner column on the
// Enterprises list, then offer "Send invitation" (POST /api/enterprises/:orgId/resend-invite).
//
// Every refusal renders as one line: CODE: message (refusalLine below).
//
// PLACES-AUTOCOMPLETE-ADD-ENTERPRISE-FORM-01: Street is an address search (OfficeAddressSearch,
// mgmt-backend's /api/places routes). A pick fills Street, City, State, Zip, Country and
// Timezone, all still editable, and the office carries the place's id, coordinates and source.

import React, { useState } from 'react';
import {
    Box, Typography, Dialog, DialogTitle, DialogContent, DialogActions, Button, Grid,
    TextField, MenuItem, FormControlLabel, Checkbox, CircularProgress, Alert, Autocomplete,
} from '@mui/material';
import BusinessIcon from '@mui/icons-material/Business';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import axios from 'axios';
import { T6_BASE, authHeader } from './EnterpriseAgentAccessTab';
import { THEME } from '../enterprise-ai/genesisData';
import OfficeAddressSearch, { officeFromPlace, placeFieldsForOffice } from './OfficeAddressSearch';

const BILLING_METHODS = [
    { value: 'invoice', label: 'Invoice' },
    { value: 'prepaid', label: 'Prepaid' },
    { value: 'cardOnFile', label: 'Card on file' },
];

const TIMEZONES = (() => {
    try { return Intl.supportedValuesOf('timeZone'); } catch (e) { return []; }
})();

const BROWSER_TZ = (() => {
    try { return Intl.DateTimeFormat().resolvedOptions().timeZone || ''; } catch (e) { return ''; }
})();

const EMPTY_FORM = {
    companyName: '',
    website: '',
    ownerFirstName: '',
    ownerLastName: '',
    ownerEmail: '',
    ownerPhone: '',
    officeLabel: 'Headquarters',
    street: '',
    unit: '',
    city: '',
    state: '',
    zip: '',
    country: 'US',
    timezone: BROWSER_TZ,
    headCount: '',
    currency: 'USD',
    billingMethod: 'invoice',
    sendInvite: true,
};

// One readable line for any failure from the panel backend or org-backend.
// org-backend refusals carry { code, message }; the staff gate and the internal key
// gate carry { error, detail }; no response at all means the panel backend is down.
export function refusalLine(err) {
    if (!err || !err.response) {
        return 'PANEL_BACKEND_UNREACHABLE: the panel backend did not answer. Check your connection and try again.';
    }
    const d = err.response.data && typeof err.response.data === 'object' ? err.response.data : {};
    const code = d.code || d.error || 'HTTP_' + err.response.status;
    const msg = d.message || (typeof d.detail === 'string' ? d.detail : '');
    return msg ? code + ': ' + msg : code;
}

export async function sendOwnerInvitation(orgId) {
    const headers = await authHeader();
    const res = await axios.post(`${T6_BASE}/api/enterprises/${encodeURIComponent(orgId)}/resend-invite`, {}, { headers });
    return res.data;
}

const fieldSx = {
    InputLabelProps: { sx: { color: THEME.textMuted } },
    InputProps: { sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } },
};

// Office fields fill from the looked-up address, never from the browser's saved addresses.
const officeSx = { ...fieldSx, inputProps: { autoComplete: 'off' } };

const Section = ({ children }) => (
    <Typography sx={{ color: THEME.mint, fontWeight: 600, fontSize: '0.85rem', mb: 1.5, mt: 1 }}>
        {children}
    </Typography>
);

export default function AddEnterpriseDialog({ open, onClose, onCreated }) {
    const [form, setForm] = useState(EMPTY_FORM);
    const [place, setPlace] = useState(null);
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);
    const [result, setResult] = useState(null);
    const [sentInvite, setSentInvite] = useState(null);
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState(null);

    const set = (field) => (e) => {
        const value = e.target.type === 'checkbox' ? e.target.checked : e.target.value;
        setForm((prev) => ({ ...prev, [field]: value }));
    };

    const reset = () => {
        setForm(EMPTY_FORM);
        setPlace(null);
        setError(null);
        setResult(null);
        setSentInvite(null);
        setSendError(null);
    };

    const close = () => {
        if (submitting || sending) return;
        reset();
        onClose();
    };

    const required = ['companyName', 'ownerFirstName', 'ownerLastName', 'ownerEmail', 'officeLabel',
        'street', 'city', 'state', 'zip', 'country', 'timezone', 'currency'];
    const formValid = required.every((k) => String(form[k] || '').trim());

    const submit = async () => {
        setSubmitting(true);
        setError(null);
        const headCount = String(form.headCount).trim() === '' ? undefined : Number(form.headCount);
        const body = {
            companyName: form.companyName.trim(),
            website: form.website.trim() || undefined,
            ownerFirstName: form.ownerFirstName.trim(),
            ownerLastName: form.ownerLastName.trim(),
            ownerEmail: form.ownerEmail.trim(),
            ownerPhone: form.ownerPhone.trim() || undefined,
            office: {
                label: form.officeLabel.trim(),
                street: form.street.trim(),
                unit: form.unit.trim() || undefined,
                city: form.city.trim(),
                state: form.state.trim(),
                zip: form.zip.trim(),
                country: form.country.trim(),
                timezone: form.timezone.trim(),
                headCount,
                ...placeFieldsForOffice(place),
            },
            headCount,
            currency: form.currency.trim().toUpperCase(),
            billingMethod: form.billingMethod,
            sendInvite: form.sendInvite,
        };
        try {
            const headers = await authHeader();
            const res = await axios.post(`${T6_BASE}/api/enterprises/provision`, body, { headers });
            console.log('[AddEnterprise] provisioned ' + JSON.stringify({
                orgId: res.data?.orgId, walletId: res.data?.walletId, inviteSent: res.data?.inviteSent,
                inviteDeferred: res.data?.inviteDeferred, askedToSend: form.sendInvite,
            }));
            setResult({ ...res.data, askedToSend: form.sendInvite });
            if (onCreated) onCreated(res.data);
        } catch (err) {
            console.error('[AddEnterprise] provision refused', err.response?.status, err.response?.data || err.message);
            setError(refusalLine(err));
        } finally {
            setSubmitting(false);
        }
    };

    const sendNow = async () => {
        setSending(true);
        setSendError(null);
        try {
            const data = await sendOwnerInvitation(result.orgId);
            if (data && data.inviteSent) {
                setSentInvite(data);
                if (onCreated) onCreated(result);
            } else {
                setSendError('OWNER_INVITE_FAILED: ' + ((data && data.inviteError) || 'org-backend reported the invitation was not sent.'));
            }
        } catch (err) {
            setSendError(refusalLine(err));
        } finally {
            setSending(false);
        }
    };

    const renderResult = () => {
        const invited = result.inviteSent === true || !!sentInvite;
        // org-backend honours sendInvite since c0f0fa4 (live in 8e580e9). If it sends with the box
        // off anyway, that is a regression there, and the card says so instead of hiding it.
        const sentAgainstWish = result.inviteSent === true && result.askedToSend === false;
        return (
            <Box sx={{ py: 1 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CheckCircleIcon sx={{ color: THEME.mint }} />
                    <Typography sx={{ fontWeight: 600, fontSize: '1.1rem', color: THEME.textPrimary }}>
                        {result.orgName || form.companyName} is set up
                    </Typography>
                </Box>
                <Box sx={{ bgcolor: THEME.card, border: `1px solid ${THEME.borderStrong}`, borderRadius: '10px', p: 2, mb: 2 }}>
                    {[
                        ['Organisation id', result.orgId],
                        ['Wallet id', result.walletId],
                        ['Currency', result.currency],
                        ['Owner', result.ownerEmail],
                    ].map(([label, value]) => (
                        <Box key={label} sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, py: 0.5 }}>
                            <Typography sx={{ color: THEME.textMuted, fontSize: '0.85rem' }}>{label}</Typography>
                            <Typography sx={{ color: THEME.textPrimary, fontSize: '0.85rem', fontFamily: 'monospace', userSelect: 'all', wordBreak: 'break-all', textAlign: 'right' }}>
                                {value || 'not returned'}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {invited ? (
                    <Alert severity="success" sx={{ bgcolor: THEME.mintDim, color: THEME.textPrimary }}>
                        Invitation sent to {(sentInvite && sentInvite.ownerEmail) || result.ownerEmail}
                    </Alert>
                ) : (
                    <Box sx={{ bgcolor: THEME.amberDim, borderRadius: '8px', p: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                        <Box>
                            <Typography sx={{ color: THEME.amber, fontWeight: 600 }}>Invitation not sent yet</Typography>
                            {result.inviteError && !result.inviteDeferred && (
                                <Typography sx={{ color: THEME.textSecondary, fontSize: '0.8rem', mt: 0.5 }}>
                                    The send failed: {result.inviteError}
                                </Typography>
                            )}
                        </Box>
                        <Button
                            onClick={sendNow}
                            disabled={sending}
                            variant="contained"
                            startIcon={sending ? <CircularProgress size={16} /> : null}
                            sx={{ bgcolor: THEME.mint, color: '#000', fontWeight: 700, textTransform: 'none', '&:hover': { bgcolor: THEME.mint } }}
                        >
                            {sending ? 'Sending...' : 'Send invitation'}
                        </Button>
                    </Box>
                )}
                {sentAgainstWish && (
                    <Alert severity="warning" sx={{ mt: 2, bgcolor: THEME.amberDim, color: THEME.textPrimary }}>
                        INVITE_SENT_DESPITE_CHECKBOX: the invitation went out although the box was off, so the Owner already has it. Tell engineering; the box should have held it back.
                    </Alert>
                )}
                {sendError && (
                    <Alert severity="error" sx={{ mt: 2, bgcolor: THEME.cardRaised, color: THEME.pink }}>{sendError}</Alert>
                )}
            </Box>
        );
    };

    return (
        <Dialog
            open={open}
            onClose={close}
            fullWidth
            maxWidth="sm"
            PaperProps={{ sx: { bgcolor: THEME.bg, color: THEME.textPrimary, borderRadius: '12px' } }}
        >
            <DialogTitle sx={{ borderBottom: `1px solid ${THEME.border}`, pb: 2 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <BusinessIcon sx={{ color: THEME.mint }} />
                    <Typography sx={{ fontWeight: 700, fontSize: '1.2rem' }}>Add enterprise</Typography>
                </Box>
            </DialogTitle>

            <DialogContent sx={{ pt: 3 }}>
                {result ? renderResult() : (
                    <>
                        <Typography sx={{ color: THEME.textSecondary, fontSize: '0.9rem', mb: 2, mt: 1 }}>
                            Creates the organisation, its first office, the Owner and a wallet in the chosen currency.
                        </Typography>

                        {error && (
                            <Alert severity="error" sx={{ mb: 2, bgcolor: THEME.cardRaised, color: THEME.pink }}>{error}</Alert>
                        )}

                        <Section>COMPANY</Section>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12} sm={7}>
                                <TextField fullWidth label="Company name *" value={form.companyName} onChange={set('companyName')} {...fieldSx} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField fullWidth label="Website" value={form.website} onChange={set('website')} placeholder="https://" {...fieldSx} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField fullWidth label="Head count" type="number" value={form.headCount} onChange={set('headCount')} {...fieldSx} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField fullWidth label="Currency *" value={form.currency} onChange={set('currency')} inputProps={{ maxLength: 3 }} {...fieldSx} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField fullWidth select label="Billing" value={form.billingMethod} onChange={set('billingMethod')} {...fieldSx}>
                                    {BILLING_METHODS.map((b) => <MenuItem key={b.value} value={b.value}>{b.label}</MenuItem>)}
                                </TextField>
                            </Grid>
                        </Grid>

                        <Section>OWNER</Section>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={6}>
                                <TextField fullWidth label="First name *" value={form.ownerFirstName} onChange={set('ownerFirstName')} {...fieldSx} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="Last name *" value={form.ownerLastName} onChange={set('ownerLastName')} {...fieldSx} />
                            </Grid>
                            <Grid item xs={12} sm={7}>
                                <TextField fullWidth label="Email *" type="email" value={form.ownerEmail} onChange={set('ownerEmail')} {...fieldSx} />
                            </Grid>
                            <Grid item xs={12} sm={5}>
                                <TextField fullWidth label="Phone" value={form.ownerPhone} onChange={set('ownerPhone')} {...fieldSx} />
                            </Grid>
                        </Grid>

                        <Section>OFFICE</Section>
                        <Grid container spacing={2} sx={{ mb: 2 }}>
                            <Grid item xs={12}>
                                <TextField fullWidth label="Label *" value={form.officeLabel} onChange={set('officeLabel')} {...officeSx} />
                            </Grid>
                            <Grid item xs={8}>
                                <OfficeAddressSearch
                                    value={form.street}
                                    country={form.country}
                                    onType={(v) => {
                                        // A new search: what is on screen is no longer the picked place.
                                        setPlace(null);
                                        setForm((prev) => ({ ...prev, street: v }));
                                    }}
                                    onPicked={(p) => {
                                        setPlace(p);
                                        setForm((prev) => ({ ...prev, ...officeFromPlace(p, prev.unit) }));
                                    }}
                                />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField fullWidth label="Suite or unit" value={form.unit} onChange={set('unit')} {...officeSx} />
                            </Grid>
                            <Grid item xs={6}>
                                <TextField fullWidth label="City *" value={form.city} onChange={set('city')} {...officeSx} />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField fullWidth label="State *" value={form.state} onChange={set('state')} {...officeSx} />
                            </Grid>
                            <Grid item xs={3}>
                                <TextField fullWidth label="Zip *" value={form.zip} onChange={set('zip')} {...officeSx} />
                            </Grid>
                            <Grid item xs={4}>
                                <TextField fullWidth label="Country *" value={form.country} onChange={set('country')} {...officeSx} />
                            </Grid>
                            <Grid item xs={8}>
                                <Autocomplete
                                    freeSolo
                                    options={TIMEZONES}
                                    inputValue={form.timezone}
                                    onInputChange={(e, v) => setForm((prev) => ({ ...prev, timezone: v || '' }))}
                                    renderInput={(params) => (
                                        <TextField
                                            {...params}
                                            label="Timezone *"
                                            inputProps={{ ...params.inputProps, autoComplete: 'off' }}
                                            InputLabelProps={{ ...params.InputLabelProps, sx: { color: THEME.textMuted } }}
                                            InputProps={{ ...params.InputProps, sx: { color: THEME.textPrimary, bgcolor: THEME.bgDeep, borderRadius: '8px' } }}
                                        />
                                    )}
                                />
                            </Grid>
                        </Grid>
                        {place && (
                            <Typography data-testid="office-place-source" sx={{ color: THEME.textMuted, fontSize: '0.75rem', mt: -1, mb: 2 }}>
                                Address from Google Maps: {place.formattedAddress}
                            </Typography>
                        )}

                        <Box sx={{ mt: 1, pt: 2, borderTop: `1px solid ${THEME.border}` }}>
                            <FormControlLabel
                                control={
                                    <Checkbox
                                        checked={form.sendInvite}
                                        onChange={set('sendInvite')}
                                        sx={{ color: THEME.mint, '&.Mui-checked': { color: THEME.mint } }}
                                    />
                                }
                                label={<Typography sx={{ color: THEME.textSecondary, fontSize: '0.9rem' }}>Send the Owner's invitation now</Typography>}
                            />
                            {!form.sendInvite && (
                                <Typography sx={{ color: THEME.textMuted, fontSize: '0.8rem', ml: 4 }}>
                                    The organisation and its wallet are created now. Send the invitation later from this list.
                                </Typography>
                            )}
                        </Box>
                    </>
                )}
            </DialogContent>

            <DialogActions sx={{ px: 3, pb: 3, pt: 1, borderTop: `1px solid ${THEME.border}` }}>
                {result ? (
                    <Button onClick={close} disabled={sending} sx={{ color: THEME.mint, textTransform: 'none', fontWeight: 600 }}>Done</Button>
                ) : (
                    <>
                        <Button onClick={close} disabled={submitting} sx={{ color: THEME.textMuted, textTransform: 'none' }}>Cancel</Button>
                        <Button
                            onClick={submit}
                            disabled={!formValid || submitting}
                            variant="contained"
                            startIcon={submitting ? <CircularProgress size={18} /> : null}
                            sx={{
                                bgcolor: THEME.mint, color: '#000', fontWeight: 700, textTransform: 'none',
                                '&:hover': { bgcolor: THEME.mint },
                                '&:disabled': { bgcolor: THEME.cardRaised, color: THEME.textMuted },
                            }}
                        >
                            {submitting ? 'Creating...' : (form.sendInvite ? 'Create and invite' : 'Create without inviting')}
                        </Button>
                    </>
                )}
            </DialogActions>
        </Dialog>
    );
}
